import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FiX, FiZap, FiEdit3, FiPlus, FiArrowRight } from 'react-icons/fi';
import {
  CreatePipelineInput,
  PipelineStage,
  DEFAULT_STAGES,
  STAGE_COLORS,
} from '../../types/fundraise';

// Type-safe icon wrappers
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const ZapIcon = FiZap as React.FC<React.SVGProps<SVGSVGElement>>;
const EditIcon = FiEdit3 as React.FC<React.SVGProps<SVGSVGElement>>;
const PlusIcon = FiPlus as React.FC<React.SVGProps<SVGSVGElement>>;
const ArrowRightIcon = FiArrowRight as React.FC<React.SVGProps<SVGSVGElement>>;

interface CreatePipelineModalProps {
  onClose: () => void;
  onSubmit: (input: CreatePipelineInput) => Promise<void>;
}

type StageType = 'default' | 'custom';

const CreatePipelineModal: React.FC<CreatePipelineModalProps> = ({
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [stageType, setStageType] = useState<StageType>('default');
  const [customStages, setCustomStages] = useState<PipelineStage[]>([]);
  const [newStageName, setNewStageName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const colorOptions = Object.keys(STAGE_COLORS);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  const handleEscapeKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) {
        onClose();
      }
    },
    [isSubmitting, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [handleEscapeKey]);

  const handleAddCustomStage = () => {
    if (!newStageName.trim()) return;

    const id = newStageName.toLowerCase().replace(/\s+/g, '_');

    // Check for duplicate stage ID (derived from name)
    if (customStages.some((s) => s.id === id)) {
      setError('A stage with a similar name already exists (names that differ only in case or spacing are not allowed)');
      return;
    }

    const colorIndex = customStages.length % colorOptions.length;

    setCustomStages([
      ...customStages,
      {
        id,
        name: newStageName.trim(),
        color: colorOptions[colorIndex],
      },
    ]);
    setNewStageName('');
    setError(null);
  };

  const handleRemoveCustomStage = (stageId: string) => {
    setCustomStages(customStages.filter((s) => s.id !== stageId));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCustomStage();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Pipeline name is required');
      return;
    }

    const stages = stageType === 'default' ? DEFAULT_STAGES : customStages;

    if (stages.length === 0) {
      setError('Please add at least one stage');
      return;
    }

    setIsSubmitting(true);

    try {
      const input: CreatePipelineInput = {
        name: name.trim(),
        target_amount: targetAmount ? (parseInt(targetAmount, 10) || 0) : 0,
        stages,
      };

      await onSubmit(input);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to create pipeline');
    } finally {
      setIsSubmitting(false);
    }
  };

  const modal = (
    <div className="su-fundraise-modal-overlay" onClick={handleOverlayClick}>
      <div className="su-fundraise-modal" role="dialog" aria-modal="true" aria-labelledby="create-pipeline-title">
        <div className="su-fundraise-modal-header">
          <div>
            <h2 id="create-pipeline-title" className="su-fundraise-modal-title">Create New Pipeline</h2>
            <p className="su-fundraise-modal-subtitle">
              Set up a new fundraising pipeline to track investors
            </p>
          </div>
          <button className="su-fundraise-modal-close" onClick={onClose} aria-label="Close modal">
            <CloseIcon width={20} height={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="su-fundraise-modal-body">
            {error && (
              <div
                style={{
                  color: '#f87171',
                  fontSize: '14px',
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                }}
              >
                {error}
              </div>
            )}

            <div className="su-fundraise-form-row">
              <div className="su-fundraise-form-group" style={{ marginBottom: 0 }}>
                <label className="su-fundraise-label">Pipeline Name *</label>
                <input
                  type="text"
                  className="su-fundraise-input"
                  placeholder="e.g., Series A Round"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="su-fundraise-form-group" style={{ marginBottom: 0 }}>
                <label className="su-fundraise-label">Target Raise Amount ($)</label>
                <input
                  type="number"
                  className="su-fundraise-input"
                  placeholder="5000000"
                  value={targetAmount}
                  onChange={(e) => setTargetAmount(e.target.value)}
                  min="0"
                />
              </div>
            </div>

            <div className="su-fundraise-form-group">
              <label className="su-fundraise-label">Pipeline Stages</label>
              <div className="su-stage-type-selector">
                <button
                  type="button"
                  className={`su-stage-type-option ${stageType === 'default' ? 'selected' : ''}`}
                  onClick={() => setStageType('default')}
                >
                  <ZapIcon className="su-stage-type-icon" width={20} height={20} />
                  <div className="su-stage-type-title">Use Default Stages</div>
                  <div className="su-stage-type-desc">
                    Pre-configured stages: Research → Contacted → Meeting → Due Diligence → Term Sheet → Closed
                  </div>
                </button>
                <button
                  type="button"
                  className={`su-stage-type-option ${stageType === 'custom' ? 'selected' : ''}`}
                  onClick={() => setStageType('custom')}
                >
                  <EditIcon className="su-stage-type-icon" width={20} height={20} />
                  <div className="su-stage-type-title">Custom Stages</div>
                  <div className="su-stage-type-desc">
                    Define your own pipeline stages from scratch
                  </div>
                </button>
              </div>
            </div>

            {stageType === 'default' && (
              <div className="su-default-stages-preview">
                <div className="su-default-stages-label">Default Stages:</div>
                {DEFAULT_STAGES.map((stage, index) => (
                  <React.Fragment key={stage.id}>
                    <div className="su-default-stage-item">
                      <span
                        className="su-kanban-column-dot"
                        style={{ backgroundColor: STAGE_COLORS[stage.color] }}
                      />
                      <span>{stage.name}</span>
                    </div>
                    {index < DEFAULT_STAGES.length - 1 && (
                      <ArrowRightIcon className="su-default-stage-arrow" width={12} height={12} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}

            {stageType === 'custom' && (
              <div className="su-custom-stages-section">
                <div className="su-custom-stages-label">Your Custom Stages:</div>
                {customStages.length === 0 ? (
                  <div className="su-custom-stages-empty">
                    No stages added yet. Add at least one stage below.
                  </div>
                ) : (
                  <div className="su-custom-stages-list">
                    {customStages.map((stage) => (
                      <div key={stage.id} className="su-custom-stage-item">
                        <span
                          className="su-custom-stage-dot"
                          style={{ backgroundColor: STAGE_COLORS[stage.color] }}
                        />
                        <span className="su-custom-stage-name">{stage.name}</span>
                        <button
                          type="button"
                          className="su-custom-stage-delete"
                          onClick={() => handleRemoveCustomStage(stage.id)}
                        >
                          <CloseIcon width={14} height={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="su-custom-stage-add">
                  <input
                    type="text"
                    className="su-custom-stage-add-input"
                    placeholder="Enter stage name..."
                    value={newStageName}
                    onChange={(e) => setNewStageName(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                  <button
                    type="button"
                    className="su-custom-stage-add-btn"
                    onClick={handleAddCustomStage}
                    disabled={!newStageName.trim()}
                  >
                    <PlusIcon width={18} height={18} />
                  </button>
                </div>
                <p className="su-fundraise-helper" style={{ marginTop: '8px' }}>
                  Tip: Common stages include Research, Outreach, First Meeting, Partner Meeting, Term Sheet, Closing
                </p>
              </div>
            )}
          </div>

          <div className="su-fundraise-modal-footer">
            <button
              type="button"
              className="su-btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="su-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Pipeline'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default CreatePipelineModal;
